import { Row } from 'antd';
import React, { FC, useMemo, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { Project } from 'api/project';
import { Field, FieldWithValue } from 'api/tasks';
import { Button } from 'components/atoms/button';
import { Drawer } from 'components/atoms/drawer';
import { Empty } from 'components/atoms/empty';
import { Input } from 'components/atoms/input';
import { GhostFields } from 'components/organisms/task-pane/task-detail/task-fields/styled';
import { addValuesToFields } from 'components/organisms/task-pane/task-detail/task-fields/task-custom-fields';
import { HelperText01, Text02 } from 'components/styles';
import { useTranslation } from 'hooks/use-translation';
import { useUser } from 'hooks/use-user';
import { ProjectOverviewExpandedItem } from './project-overview-expanded-item';

const StyledDrawer = styled(Drawer)`
  .ant-drawer-header {
    background: var(--private-01);
    height: 70px;
  }

  .ant-drawer-body {
    padding: var(--spacing-05) var(--spacing-06) var(--spacing-10);
  }
`;
const SubTitle = styled.p`
  ${HelperText01};
  ${Text02};
`;

const StyledEmptyMessage = styled.h4`
  margin-bottom: 0px;
`;

const StyledResetButton = styled(Button)`
  padding: 0px;
`;
const StyledEmpty = styled(Empty)`
  padding-left: var(--spacing-08);
`;

const StyledSearchInput = styled(Input)`
  margin-bottom: var(--spacing-05);
`;

interface Props {
  visible: boolean;
  projectData: Project;
  defaultFields: Field[];
  customFields: Field[];
  isDefaultFieldsReadOnly: boolean;
  closeDrawer: () => void;

  onCustomFieldChange(fieldValue: any, { fieldId }: FieldWithValue): void;

  onDefaultFieldChange({
    startDate,
    dueDate,
    annualizedRecurringRevenue,
    projectFee,
  });
}

export const ProjectOverviewExpanded: FC<Props> = ({
  visible,
  projectData,
  closeDrawer,
  defaultFields,
  customFields,
  isDefaultFieldsReadOnly,
  onCustomFieldChange,
  onDefaultFieldChange,
}) => {
  const { user } = useUser();
  const searchInputRef = useRef<any>(null);
  const [searchText, setSearchText] = useState<string>('');
  const fieldsWithValues = useMemo(() => {
    return addValuesToFields(customFields, projectData.fields);
  }, [customFields, projectData.fields]);

  const searchPlaceholder = useTranslation(
    'project.overview.search_placeholder',
  );

  const fieldsToDisplay = useMemo(
    () =>
      [...defaultFields, ...fieldsWithValues].filter(({ fieldName }) =>
        fieldName.toLowerCase().includes(searchText.toLowerCase()),
      ),
    [searchText, defaultFields, fieldsWithValues],
  );

  return (
    <StyledDrawer
      width={680}
      onClose={closeDrawer}
      visible={visible}
      title={
        <>
          <strong>
            <FormattedMessage id="project.overview.project_details.title" />
          </strong>
          <SubTitle>
            <FormattedMessage
              id="project.overview.project_details.sub_title"
              values={{ companyName: user.company.companyName }}
            />
          </SubTitle>
        </>
      }
      footer={
        <Button
          type="primary"
          onClick={closeDrawer}
          data-cy="project.overview.see-more-fields.done"
        >
          {useTranslation('done')}
        </Button>
      }
    >
      <StyledSearchInput
        autoFocus
        ref={searchInputRef}
        placeholder={searchPlaceholder}
        value={searchText}
        onChange={(e) => setSearchText(e.target.value)}
      />
      {fieldsToDisplay.length ? (
        <GhostFields>
          <Row gutter={[16, 4]}>
            {fieldsToDisplay.map((field) => {
              return (
                <ProjectOverviewExpandedItem
                  field={field}
                  isReadOnly={isDefaultFieldsReadOnly}
                  values={projectData.fields}
                  onCustomFieldChange={onCustomFieldChange}
                  onDefaultFieldChange={onDefaultFieldChange}
                  projectData={projectData}
                  defaultFields={defaultFields}
                />
              );
            })}
          </Row>
        </GhostFields>
      ) : (
        <StyledEmpty>
          <div>
            <StyledEmptyMessage>
              <FormattedMessage id="project.overview.search.empty" />
            </StyledEmptyMessage>
            <StyledResetButton
              onClick={() => {
                if (searchInputRef?.current) {
                  searchInputRef.current.focus();
                }

                return setSearchText('');
              }}
              type="link"
              size="small"
            >
              <FormattedMessage id="reset_search" />
            </StyledResetButton>
          </div>
        </StyledEmpty>
      )}
    </StyledDrawer>
  );
};
